import { useEffect, useRef, useState } from "react";

const TRAIL_DURATION_MS = 1000;

export function resolveClientActiveMenuId(pathname, menus = []) {
  return (
    menus.find((menu) => {
      if (pathname === menu.path) return true;
      return (
        menu.path.length > 1 && pathname.startsWith(`${menu.path}/`)
      );
    })?.id ?? null
  );
}

function computeNextTrail(current, previousMenuId, activeMenuId) {
  let recent2 = null;

  if (
    current.recent1 &&
    current.recent1 !== previousMenuId &&
    current.recent1 !== activeMenuId
  ) {
    recent2 = current.recent1;
  } else if (
    current.recent2 &&
    current.recent2 !== previousMenuId &&
    current.recent2 !== activeMenuId
  ) {
    recent2 = current.recent2;
  }

  return { recent1: previousMenuId, recent2 };
}

export function useClientNavTrail(activeMenuId, enabled) {
  const [trail, setTrail] = useState({ recent1: null, recent2: null });
  const trailRef = useRef({ recent1: null, recent2: null });
  const prevActiveRef = useRef(null);
  const timersRef = useRef(new Map());

  const clearTimer = (slot, menuId) => {
    const key = `${slot}:${menuId}`;
    const existing = timersRef.current.get(key);
    if (existing) {
      clearTimeout(existing);
      timersRef.current.delete(key);
    }
  };

  const scheduleRemoval = (menuId, slot) => {
    if (!menuId) return;

    const key = `${slot}:${menuId}`;
    clearTimer(slot, menuId);

    const timer = setTimeout(() => {
      setTrail((current) => {
        if (current[slot] !== menuId) return current;
        const next = { ...current, [slot]: null };
        trailRef.current = next;
        return next;
      });
      timersRef.current.delete(key);
    }, TRAIL_DURATION_MS);

    timersRef.current.set(key, timer);
  };

  useEffect(() => {
    if (!enabled) {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
      trailRef.current = { recent1: null, recent2: null };
      prevActiveRef.current = null;
      setTrail({ recent1: null, recent2: null });
      return undefined;
    }

    if (!activeMenuId) return undefined;

    const previousMenuId = prevActiveRef.current;
    if (activeMenuId === previousMenuId) return undefined;

    prevActiveRef.current = activeMenuId;

    if (!previousMenuId) return undefined;

    const demotedMenuId = trailRef.current.recent1;
    const droppedRecent2 = trailRef.current.recent2;
    const nextTrail = computeNextTrail(
      trailRef.current,
      previousMenuId,
      activeMenuId,
    );

    trailRef.current = nextTrail;
    setTrail(nextTrail);

    if (
      droppedRecent2 &&
      droppedRecent2 !== nextTrail.recent1 &&
      droppedRecent2 !== nextTrail.recent2
    ) {
      clearTimer("recent1", droppedRecent2);
      clearTimer("recent2", droppedRecent2);
    }

    if (
      demotedMenuId &&
      demotedMenuId !== nextTrail.recent1 &&
      demotedMenuId !== nextTrail.recent2
    ) {
      clearTimer("recent1", demotedMenuId);
      clearTimer("recent2", demotedMenuId);
    }

    if (demotedMenuId && demotedMenuId === nextTrail.recent2) {
      clearTimer("recent1", demotedMenuId);
      scheduleRemoval(demotedMenuId, "recent2");
    }

    scheduleRemoval(previousMenuId, "recent1");

    if (
      nextTrail.recent2 &&
      nextTrail.recent2 !== demotedMenuId
    ) {
      scheduleRemoval(nextTrail.recent2, "recent2");
    }

    return undefined;
  }, [activeMenuId, enabled]);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    [],
  );

  return trail;
}
